export interface CalendarDay {
  iso: string; // YYYY-MM-DD
  day: number;
  inCurrentMonth: boolean;
}

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_LABELS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function getWeekdayLabels(): string[] {
  return WEEKDAY_LABELS;
}

export function toIsoDate(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

export function todayIso(): string {
  const now = new Date();
  return toIsoDate(now.getFullYear(), now.getMonth(), now.getDate());
}

export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return toIsoDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

export function getMonthLabel(year: number, month: number): string {
  return `${MONTH_LABELS[month]} ${year}`;
}

export function formatIsoShort(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTH_LABELS_SHORT[m - 1]} ${d}`;
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Sep 1, 2026 (Tue)" — the fuller date used on booking/invoice detail. */
export function formatIsoLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const weekday = WEEKDAY_SHORT[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${MONTH_LABELS_SHORT[m - 1]} ${d}, ${y} (${weekday})`;
}

/**
 * Supplier timestamps (ETG `free_cancellation_before`, penalty `start_at` /
 * `end_at`) are UTC+0 but carry no zone ("2026-12-07T00:00:00"). `new Date()`
 * would read those as the viewer's local time and shift the deadline by the
 * viewer's offset, so a zone-less date-time is read as UTC here.
 */
export function parseSupplierDateTime(iso: string): Date {
  const value = iso.trim();
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return new Date(hasZone || !value.includes('T') ? value : `${value}Z`);
}

/** True while a supplier deadline (e.g. free cancellation) is still ahead. */
export function isBeforeSupplierDeadline(iso: string | null | undefined, now: number = Date.now()): boolean {
  if (!iso) return false;
  const deadline = parseSupplierDateTime(iso).getTime();
  return Number.isFinite(deadline) && deadline > now;
}

/** Cancellation deadlines must display in UTC+0 per ETG best practice §3.8. */
export function formatUtcDateTime(iso: string): string {
  const date = parseSupplierDateTime(iso);
  const month = MONTH_LABELS_SHORT[date.getUTCMonth()];
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${month} ${date.getUTCDate()}, ${date.getUTCFullYear()}, ${hours}:${minutes} UTC+0`;
}

/** Calendar-date-only form of a cancellation deadline ("Sep 19, 2026"), for
 * compact summary rows. The exact UTC+0 timestamp (formatUtcDateTime) stays
 * required wherever the deadline is the primary, standalone disclosure. */
export function formatUtcDateShort(iso: string): string {
  const date = parseSupplierDateTime(iso);
  const month = MONTH_LABELS_SHORT[date.getUTCMonth()];
  return `${month} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

const WEEKDAY_FULL = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

export interface DateParts {
  day: number;
  monthYear: string;
  // "Oct" this year, "Oct 2027" otherwise: the narrow homepage date fields
  // truncated "25 Oct 2026" to "25 Oct 20…".
  monthLabel: string;
  weekday: string;
}

/** Breaks an ISO date into the day-number / month-year / weekday pieces used
 * by the homepage search box's high-hierarchy date fields ("23" / "Sep 2026"
 * / "Monday"). */
export function formatDateParts(iso: string, currentYear: number = new Date().getFullYear()): DateParts {
  const [y, m, d] = iso.split('-').map(Number);
  const weekday = WEEKDAY_FULL[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  const monthYear = `${MONTH_LABELS_SHORT[m - 1]} ${y}`;
  return { day: d, monthYear, monthLabel: y === currentYear ? MONTH_LABELS_SHORT[m - 1] : monthYear, weekday };
}

export function formatRangeLabel(startIso: string | null, endIso: string | null): string {
  if (!startIso) return 'Select dates';
  if (!endIso) return formatIsoShort(startIso);
  return `${formatIsoShort(startIso)} to ${formatIsoShort(endIso)}`;
}

/** Drops the repeated month and the "to" ("Sep 1 – 6") so a date range
 * still fits a half-width field on a phone instead of truncating away the
 * checkout date. */
export function formatRangeLabelCompact(startIso: string | null, endIso: string | null): string {
  if (!startIso) return 'Select dates';
  if (!endIso) return formatIsoShort(startIso);
  const [, startMonth] = startIso.split('-').map(Number);
  const [, endMonth, endDay] = endIso.split('-').map(Number);
  if (startMonth === endMonth) return `${formatIsoShort(startIso)} – ${endDay}`;
  return `${formatIsoShort(startIso)} – ${formatIsoShort(endIso)}`;
}

export function getMonthGrid(year: number, month: number): CalendarDay[][] {
  const firstOfMonth = new Date(Date.UTC(year, month, 1));
  const startWeekday = firstOfMonth.getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const daysInPrevMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const prev = addMonths(year, month, -1);
  const next = addMonths(year, month, 1);

  const cells: CalendarDay[] = [];

  for (let i = startWeekday - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    cells.push({ iso: toIsoDate(prev.year, prev.month, day), day, inCurrentMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ iso: toIsoDate(year, month, day), day, inCurrentMonth: true });
  }
  let nextMonthDay = 1;
  while (cells.length % 7 !== 0) {
    cells.push({ iso: toIsoDate(next.year, next.month, nextMonthDay), day: nextMonthDay, inCurrentMonth: false });
    nextMonthDay++;
  }

  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
